import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React from "react";

import { config } from "../config";
import { useEventsApi } from "../hooks/useEventsApi";
import type { VehicleSoftwareScanBasis } from "../shared/vehicleSoftwareEventScan";
import { formatLocalDateTime } from "../utils/formatLocalDateTime";

export type VehicleSoftwareScanUsageRow = {
  report_id: number;
  software_name: string;
  uploaded_at: string;
  git_commit: string | null | undefined;
  pull_request_id: string | null | undefined;
  pull_request_url: string | null | undefined;
  /** Legacy uploads that only sent pipeline build id */
  build_id: string | null | undefined;
  source_branch: string | null | undefined;
  matched_scan_event_codes: number[];
};

type UsageResponse = {
  comparison_codes: number[];
  scan_basis: VehicleSoftwareScanBasis;
  results: VehicleSoftwareScanUsageRow[];
};

type Props = {
  eventId: number;
};

const EventVehicleSoftwareScanAccordion = ({ eventId }: Props) => {
  const { callApi } = useEventsApi();
  const [expanded, setExpanded] = React.useState(false);
  const [usageScanBasis, setUsageScanBasis] = React.useState<VehicleSoftwareScanBasis>("latest_per_software");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [payload, setPayload] = React.useState<UsageResponse | null>(null);

  React.useEffect(() => {
    if (!expanded || !eventId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const q = new URLSearchParams();
        q.set("scan_basis", usageScanBasis);
        const raw = await callApi(
          `${config.apiUrl}/api/events/${eventId}/vehicle-software-scan-usage/?${q.toString()}`,
          "GET"
        );
        if (cancelled) return;
        if (raw == null) {
          setError("Not signed in.");
          setPayload(null);
          return;
        }
        setPayload(raw as UsageResponse);
      } catch {
        if (!cancelled) {
          setError("Could not load vehicle software scan usage.");
          setPayload(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [expanded, eventId, callApi, usageScanBasis]);

  return (
    <Accordion expanded={expanded} onChange={(_, next) => setExpanded(next)} disableGutters sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>Vehicle software scans</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Pipeline CSV uploads that contained this event&apos;s code (
          <code>event_code</code> or numeric <code>event_code_old</code>). Choose whether matches are counted from the
          latest upload per software only, or from any historical upload.
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
          Comparison basis
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={usageScanBasis}
          onChange={(_, v: VehicleSoftwareScanBasis | null) => {
            if (v != null) setUsageScanBasis(v);
          }}
          sx={{ mb: 1.5 }}
        >
          <ToggleButton value="latest_per_software">Latest per software</ToggleButton>
          <ToggleButton value="all_reports">All uploads</ToggleButton>
        </ToggleButtonGroup>
        {error && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        {loading && (
          <Stack alignItems="center" py={1}>
            <CircularProgress size={24} />
          </Stack>
        )}
        {!loading && payload && payload.results.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No matching scan rows yet (comparison codes checked: {payload.comparison_codes.join(", ")}).
          </Typography>
        )}
        {!loading && payload && payload.results.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Software</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Commit</TableCell>
                  <TableCell>Pull request</TableCell>
                  <TableCell>Matched CSV codes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payload.results.map((row) => (
                  <TableRow key={row.report_id}>
                    <TableCell>{row.software_name}</TableCell>
                    <TableCell>{formatLocalDateTime(row.uploaded_at)}</TableCell>
                    <TableCell>
                      {row.git_commit ? (
                        <Box component="code" sx={{ fontSize: "0.8rem", wordBreak: "break-all" }}>
                          {row.git_commit}
                        </Box>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {row.pull_request_url ? (
                        <Link
                          href={row.pull_request_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                        >
                          {row.pull_request_id ? `PR #${row.pull_request_id}` : "Open PR"}
                        </Link>
                      ) : row.pull_request_id ? (
                        `PR #${row.pull_request_id}`
                      ) : row.build_id ? (
                        <Typography variant="body2" color="text.secondary" component="span">
                          Build {row.build_id}
                        </Typography>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Box component="code" sx={{ fontSize: "0.8rem" }}>
                        {row.matched_scan_event_codes.join(", ")}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default EventVehicleSoftwareScanAccordion;
